import { useNavigate } from 'react-router-dom';
import { useWorkspaceProjection } from '../../hooks/useWorkspaces';
import { useExperienceMode } from '../../hooks/useExperienceMode';
import { WorkspaceStatusIcons } from './WorkspaceResourceCards';
import { displayValue } from './workspaceDisplay';

interface WorkspaceStatusBarProps {
  workspaceId: string;
  onDetach?: () => void;
}

export function WorkspaceStatusBar({ workspaceId, onDetach }: WorkspaceStatusBarProps) {
  const navigate = useNavigate();
  const { projection } = useWorkspaceProjection(workspaceId);
  const { mode } = useExperienceMode();

  if (!projection) return null;

  const advanced = mode === 'advanced';
  const nameDetail = [
    `${projection.name}${projection.binding?.folder_path ? ` — ${projection.binding.folder_path}` : ''}`,
    advanced
      ? `${displayValue(projection.lifecycle)} · ${displayValue(projection.assignment.routing_class)}`
      : null,
    'File tools are available to the agent',
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

  return (
    <div
      className="flex items-center gap-2 rounded-md bg-primary/5 px-2 py-1 text-caption"
      data-testid="workspace-status-bar"
    >
      <span className="font-medium text-muted-foreground">Workspace</span>
      <button
        onClick={() => navigate('/workspaces')}
        className="font-medium text-foreground hover:underline"
        title={nameDetail}
      >
        {projection.name}
      </button>
      <WorkspaceStatusIcons projection={projection} showIndex={advanced} />
      {onDetach && (
        <button
          type="button"
          data-testid="workspace-detach"
          onClick={onDetach}
          title="Detach this workspace — the agent loses file & git tools"
          aria-label="Detach workspace"
          className="ml-1 rounded px-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          ×
        </button>
      )}
    </div>
  );
}
